import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isTestUnlocked } from '../../../utils/subscriptionHelper';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWritingQuestions, saveWritingAttempt } from '../../../api/index';
import WriscoreGraph from '../WriscoreGraph';
import LoadingAnimation from '../../../pages/LoadingAnimation';
import EngdemyLogo from '../../../assets/Images/EngdemyUploaded.png';
import '../../../componets/Listening.css';
import './WritingTest.css';

const WritingTest = ({ testNumber: propTestNumber, onComplete } = {}) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const API_URL = (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.startsWith("http") && !process.env.REACT_APP_API_BASE_URL.includes("localhost:5000"))
        ? process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')
        : "https://engdemyeducation.com";

    const urlTestNumber =
        propTestNumber || params.testNumber || params.id || params.number || location.pathname.split('/').pop();
    const parsedNum = parseInt(urlTestNumber);
    const initialTestNumber = (!isNaN(parsedNum) && parsedNum > 0) ? parsedNum : 1;

    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [answer, setAnswer] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [selectedOption, setSelectedOption] = useState('');
    const [scores, setScores] = useState(null);
        const [showGraph, setShowGraph] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fatalErrorMsg, setFatalErrorMsg] = useState('');
    const [showFatalError, setShowFatalError] = useState(false);

    const isShowingResults = location.state?.showResults;
    const [timeRemaining, setTimeRemaining] = useState(0);
        const [isTestStarted, setIsTestStarted] = useState(false);
    const [isTestCompleted, setIsTestCompleted] = useState(false);
    const [showInstructions, setShowInstructions] = useState(!isShowingResults);
    const [showFormatModal, setShowFormatModal] = useState(false);
    const [showSampleModal, setShowSampleModal] = useState(false);
    const [sampleData, setSampleData] = useState(null);
    const [isSampleLoading, setIsSampleLoading] = useState(false);
    const [sampleError, setSampleError] = useState('');
    const [taskSamples, setTaskSamples] = useState({ task1: null, task2: null });
    const [taskSampleLoading, setTaskSampleLoading] = useState({ task1: false, task2: false });
    const [taskSampleErrors, setTaskSampleErrors] = useState({ task1: '', task2: '' });
    const [sampleOpen, setSampleOpen] = useState({ task1: true, task2: true });
    const [copiedSample, setCopiedSample] = useState({ task1: false, task2: false });
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [selectedTestNumber, setSelectedTestNumber] = useState(initialTestNumber);
    
    const [taskScores, setTaskScores] = useState({ task1: null, task2: null });

    // Use ref to persist task scores across closures
    const taskScoresRef = useRef({ task1: null, task2: null });

    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);

    // Normalize user data
    const getStoredUser = () => {
        try {
            if (reduxUser?.result) return reduxUser.result;
            if (reduxUser?.email || reduxUser?._id) return reduxUser;
            const raw = localStorage.getItem('user_info');
            if (raw) {
                const localUser = JSON.parse(raw);
                if (localUser?.result) return localUser.result;
                if (localUser?.email || localUser?._id) return localUser;
            }
            const authRaw = localStorage.getItem('authData');
            if (authRaw) {
                const localAuth = JSON.parse(authRaw);
                if (localAuth?.result) return localAuth.result;
                if (localAuth?.email || localAuth?._id) return localAuth;
            }
        } catch (e) {}
        return null;
    };

    const [currentUser, setCurrentUser] = useState(getStoredUser);

    useEffect(() => {
        const storedUser = getStoredUser();
        if (storedUser) {
            setCurrentUser(storedUser);
        }
        if (!reduxUser && storedUser) {
            const raw = localStorage.getItem("user_info");
            let dataToDispatch = null;
            try {
                const parsed = raw ? JSON.parse(raw) : null;
                if (parsed?.result) dataToDispatch = parsed;
            } catch (e) {}
            if (!dataToDispatch) {
                dataToDispatch = { result: storedUser, ...storedUser };
            }
            dispatch({ type: 'AUTH', data: dataToDispatch });
        }
    }, [reduxUser, dispatch]);

    // Auto-dismiss fatal error banner after 8 seconds
    useEffect(() => {
        if (showFatalError) {
            const timer = setTimeout(() => {
                setShowFatalError(false);
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [showFatalError]);

    // Sync selectedTestNumber with propTestNumber and params.testNumber
    useEffect(() => {
        if (propTestNumber) {
            setSelectedTestNumber(Number(propTestNumber));
        } else if (params.testNumber) {
            const n = Number(params.testNumber);
            if (!isNaN(n) && n > 0) setSelectedTestNumber(n);
        }
    }, [propTestNumber, params.testNumber]);

    // Handle direct navigation via testNumber param
    useEffect(() => {
        // Handle showResults from CheckProgress
        if (location.state?.showResults && location.state?.savedAttempts) {
             const savedAttempts = location.state.savedAttempts;
             
             // Populate taskScores with the saved attempts
             const newTaskScores = { task1: null, task2: null };

             // Helper: remap DB field names → API/display field names
             const remapDbScores = (dbScores, typedAnswer) => ({
                 // Remap DB schema names to API response names used by WriscoreGraph
                 taskFulfillment: dbScores?.taskFulfillmentScore || 0,
                 coherence: dbScores?.contentScore || 0,
                 grammar: dbScores?.readabilityScore || 0,
                 vocabulary: dbScores?.vocabularyScore || 0,
                 total: dbScores?.overallScore || 0,
                 // Keep originals too as fallback
                 ...dbScores,
                 originalText: typedAnswer || dbScores?.originalText || '',
             });

             if (savedAttempts.task1) {
                 newTaskScores.task1 = remapDbScores(
                     savedAttempts.task1.scores,
                     savedAttempts.task1.typedAnswer
                 );
             }
             if (savedAttempts.task2) {
                 newTaskScores.task2 = remapDbScores(
                     savedAttempts.task2.scores,
                     savedAttempts.task2.typedAnswer
                 );
             }
             
             setTaskScores(newTaskScores);
             taskScoresRef.current = newTaskScores;
             setIsTestCompleted(true);
             // Also set selectedTestNumber so the UI knows which test we are viewing
             if (savedAttempts.task1?.testNumber || savedAttempts.task2?.testNumber) {
                setSelectedTestNumber(parseInt(savedAttempts.task1?.testNumber || savedAttempts.task2?.testNumber));
             }
             return; // Skip the rest of the effect
        }

        const testNum = parseInt(urlTestNumber);
        if (testNum >= 1) {
            if (isLockedTest(testNum)) {
                alert('This test is locked. Please upgrade your account to access it.');
                navigate('/pricing');
                return;
            }
            setSelectedTestNumber(testNum);

            setIsTestStarted(false);
            setShowInstructions(true);
        }
    }, [urlTestNumber, navigate, location.state]);

    // Speech Synthesis for Instructions
    const stopSpeech = useCallback(() => {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    const playSpeech = useCallback((text) => {
        if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.lang = "en-US";

        try {
            const voices = window.speechSynthesis.getVoices();
            const enVoice = voices.find(v => v.lang.startsWith("en-") && (v.name.includes("Natural") || v.name.includes("Google") || v.name.includes("Samantha") || v.name.includes("David") || v.name.includes("Zira")));
            if (enVoice) utterance.voice = enVoice;
        } catch (e) {}

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    }, []);

    const instructionBullets = [
        "On the official test, if you do not finish Task 1 in 27 minutes, the screen will move to Task 2. You cannot go back to Task 1. However, in this practice test, in order to move forward in the test you must click on \"NEXT.\"",
        "You have 53 minutes to complete this practice Writing Test."
    ];

    const toggleSpeech = () => {
        if (isSpeaking) {
            stopSpeech();
        } else {
            playSpeech("Writing Test Instructions. " + instructionBullets.join(" "));
        }
    };

    useEffect(() => {
        if (showInstructions) {
            playSpeech("Writing Test Instructions. " + instructionBullets.join(" "));
        } else {
            stopSpeech();
        }
        return () => stopSpeech();
    }, [showInstructions, playSpeech, stopSpeech]);

    const handleGlobalNext = () => {
        if (showInstructions) {
            stopSpeech();
            setShowInstructions(false);
            setIsTestStarted(true);
            setTimeRemaining(questions[0]?.taskNumber === '2' ? 26 * 60 : 27 * 60);
        } else {
            handleNextTask(false);
        }
    };

    const handleGlobalBack = () => {
        stopSpeech();
        if (showInstructions) {
            navigate('/dashboard?section=writing');
        } else {
            if (window.confirm("Are you sure you want to return to Instructions? Your current input will be lost.")) {
                setShowInstructions(true);
                setIsTestStarted(false);
            }
        }
    };

    
    const isLockedTest = (num) => {
        return !isTestUnlocked(num);
    };

        
    // Fetch questions
    useEffect(() => {
        const getQuestions = async (testNumber) => {
            try {
                const { data } = await fetchWritingQuestions(testNumber);
                const filtered = data
                    .filter(
                        (q) =>
                            q.testNumber === testNumber.toString() &&
                            (q.taskNumber === '1' || q.taskNumber === '2')
                    )
                    .sort((a, b) => a.taskNumber - b.taskNumber);

                setQuestions(filtered);
                setCurrentQuestion(filtered[0]);
                setCurrentIndex(0);
                setTimeRemaining(filtered[0]?.taskNumber === '1' ? 27 * 60 : 26 * 60);
            } catch (err) {
                console.error('Error fetching questions:', err);
            }
        };

        if (selectedTestNumber) getQuestions(selectedTestNumber);
    }, [selectedTestNumber]);

    // Timer
    useEffect(() => {
        if (!isTestStarted || timeRemaining <= 0) return;

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev > 0) return prev - 1;
                clearInterval(timer);
                handleNextTask(true);
                return 0;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeRemaining, isTestStarted]);

    const handleTextChange = (e) => {
        const val = e.target.value;
        setAnswer(val);
        setWordCount(val.split(/\s+/).filter((w) => w).length);
    };

    const handleOptionChange = (e) => setSelectedOption(e.target.value);

    // Save attempt to backend
    const saveWritingTestAttempt = async (typedAnswer, scores) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user_info'));
            const userId = userInfo?.result?._id;

            if (!userId || !currentQuestion?._id || !selectedTestNumber || !currentQuestion?.taskNumber) {
                console.error('❌ Missing data for saving attempt');
                return;
            }

            // Remap API field names → DB schema field names
            // API returns: { total, taskFulfillment, coherence, grammar, vocabulary, transitions, wordCount, ... }
            // DB schema expects: { overallScore, taskFulfillmentScore, contentScore, readabilityScore, vocabularyScore }
            const mappedScores = {
                overallScore: scores?.total ?? scores?.overallScore ?? 0,
                taskFulfillmentScore: scores?.taskFulfillment ?? scores?.taskFulfillmentScore ?? 0,
                contentScore: scores?.coherence ?? scores?.contentScore ?? 0,
                readabilityScore: scores?.grammar ?? scores?.readabilityScore ?? 0,
                vocabularyScore: scores?.vocabulary ?? scores?.vocabularyScore ?? 0,
            };

            const payload = {
                userId,
                testNumber: selectedTestNumber,
                taskNumber: currentQuestion.taskNumber,
                questionId: currentQuestion._id,
                typedAnswer,
                scores: mappedScores,
            };

            await saveWritingAttempt(payload);
            console.log('✅ Writing attempt saved successfully. Scores:', mappedScores);
        } catch (err) {
            console.error('❌ Error saving attempt:', err);
        }
    };


    // Submit current task (called by timer expiry or NEXT button without prior scoring)
    const submitCurrentTask = async () => {
        if (!answer.trim()) return;

        try {
            // Use same task-specific endpoint as handleSubmit
            const endpoint = currentQuestion?.taskNumber === '1' ? '/email' : '/survey';
            const fullUrl = `${API_URL}/analyzeWriting${endpoint}`;

            const questionContext = [
                currentQuestion?.questionLeft,
                currentQuestion?.questionRight,
                currentQuestion?.questionDetail,
                currentQuestion?.radioSelectOne,
                currentQuestion?.radioSelectTwo
            ].filter(Boolean).join('\n\n');

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ answer, question: questionContext }),
            });

            if (!response.ok) throw new Error('Failed to analyze');

            const data = await response.json();

            // Relevance warning is no longer a blocker — scores are still provided
            if (data.extractedScores?.relevanceWarning) {
                console.log('⚠️ Relevance warning (still scored):', data.extractedScores.relevanceWarning);
            }

            await saveWritingTestAttempt(answer, data.extractedScores);

            // Include the answer as originalText
            const scoresWithText = {
                ...data.extractedScores,
                originalText: data.extractedScores.originalText || answer
            };

            if (currentQuestion.taskNumber === '1') {
                taskScoresRef.current.task1 = scoresWithText;
                setTaskScores((prev) => ({ ...prev, task1: scoresWithText }));
            } else if (currentQuestion.taskNumber === '2') {
                taskScoresRef.current.task2 = scoresWithText;
                setTaskScores({
                    task1: taskScoresRef.current.task1,
                    task2: scoresWithText
                });
            }

            return scoresWithText;
        } catch (err) {
            console.error('❌ Error in submitCurrentTask:', err.message);
        }
    };

    // Next task logic
    const handleNextTask = async (auto = false) => {
        // Only submit if not already submitted via handleSubmit - use ref for reliable check
        const taskNum = currentQuestion?.taskNumber;
        const alreadySubmitted =
            (taskNum === '1' && taskScoresRef.current.task1) ||
            (taskNum === '2' && taskScoresRef.current.task2);

        if (!alreadySubmitted) {
            await submitCurrentTask();
        }

        if (currentIndex < questions.length - 1) {
            const nextIndex = currentIndex + 1;
            setCurrentIndex(nextIndex);
            setCurrentQuestion(questions[nextIndex]);
            setAnswer('');
            setWordCount(0);
            setSelectedOption('');
            setScores(null);
            setShowGraph(false);
            setTimeRemaining(questions[nextIndex].taskNumber === '1' ? 27 * 60 : 26 * 60);
        } else {
            setIsTestCompleted(true);
        }
    };

    
    const handleSubmit = async () => {
        try {
            // show animation/message immediately
            setIsLoading(true);
            setShowFatalError(false);
            setFatalErrorMsg('');

            console.log('📤 Submitting writing test...');
            console.log('📝 Task Number:', currentQuestion?.taskNumber);
            console.log('✍️ Answer length:', answer.length);
            console.log('📊 Word count:', wordCount);

            // Frontend validation: Check if word count is at least 100
            if (wordCount < 100) {
                setFatalErrorMsg(`Your response needs a bit more detail — it’s currently ${wordCount} words. Aim for at least 100 words and try again!`);
                setShowFatalError(true);
                setIsLoading(false);
                return;
            }

            // Determine which endpoint to use based on task number
            const endpoint = currentQuestion?.taskNumber === '1' ? '/email' : '/survey';
            const fullUrl = `${API_URL}/analyzeWriting${endpoint}`;

            console.log('🎯 Calling endpoint:', fullUrl);

            const questionContext = [
                currentQuestion?.questionLeft,
                currentQuestion?.questionRight,
                currentQuestion?.questionDetail,
                currentQuestion?.radioSelectOne,
                currentQuestion?.radioSelectTwo
            ].filter(Boolean).join('\n\n');

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    answer,
                    question: questionContext
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ API Response:', data);

            const { extractedScores } = data;

            // Handle Relevance Warning — show encouraging banner but CONTINUE with scoring
            if (extractedScores.relevanceWarning) {
                setFatalErrorMsg(extractedScores.relevanceWarning);
                setShowFatalError(true);
                // Don't return — fall through to show scores alongside the warning
            }

            console.log('📊 Extracted Scores:', extractedScores);
            console.log('📝 Has originalText?', !!extractedScores.originalText);
            console.log('❌ Grammar errors:', extractedScores.grammarErrors?.length || 0);
            console.log('✏️ Punctuation errors:', extractedScores.punctuationErrors?.length || 0);

            // Set scores for WriscoreGraph
            setScores(extractedScores);

            // Save the attempt
            await saveWritingTestAttempt(answer, extractedScores);

            // Save scores to taskScores for the result page
            // Include the answer as originalText if not already present in extractedScores
            const scoresWithText = {
                ...extractedScores,
                originalText: extractedScores.originalText || answer
            };

            console.log('💾 Saving scores for task:', currentQuestion.taskNumber);
            console.log('📊 Scores with text:', scoresWithText);

            if (currentQuestion.taskNumber === '1') {
                taskScoresRef.current.task1 = scoresWithText;
                setTaskScores((prev) => ({ ...prev, task1: scoresWithText }));
            } else if (currentQuestion.taskNumber === '2') {
                taskScoresRef.current.task2 = scoresWithText;
                // Merge with existing task1 from ref to ensure both are preserved
                setTaskScores({
                    task1: taskScoresRef.current.task1,
                    task2: scoresWithText
                });
            }

            // If this is the last task (Task 2), show result page directly
            if (currentQuestion.taskNumber === '2') {
                setIsTestCompleted(true);
            } else {
                // Show the graph modal/component for Task 1
                setShowGraph(true);
            }

        } catch (error) {
            console.error('❌ Submission error:', error);
            alert('Failed to analyze your writing. Please try again.');
        } finally {
            // always hide loading once done (success or error)
            setIsLoading(false);
        }
    };

    // Helpers to parse prompt content
    const cleanQuestionLeft = (text) => {
        if (!text) return '';
        return text
            .replace(/^!+\s*Read the following information\s*:\s*/i, '')
            .replace(/^Read the following information\s*:\s*/i, '')
            .replace(/^!+\s*/, '')
            .trim();
    };

    const parseRightPaneContent = (text, detail) => {
        if (!text) return { heading: '', bullets: [] };

        const cleanText = text.replace(/^!+\s*/, '').trim();

        // Check for "following things:"
        const splitRegex = /(?:following things\s*:\s*|following\s*:\s*)/i;
        const match = cleanText.match(splitRegex);

        if (match) {
            const idx = match.index + match[0].length;
            let heading = cleanText.substring(0, idx).trim();
            heading = heading.replace(/\s+:\s*$/, ':');
            if (!heading.endsWith(':')) heading += ':';
            const remainder = cleanText.substring(idx).trim();

            let bullets = [];
            if (remainder.includes('\n')) {
                bullets = remainder.split('\n').map(l => l.replace(/^[•\-\*\d\.\)]+\s*/, '').trim()).filter(Boolean);
            } else if (remainder.includes('•') || remainder.includes('- ')) {
                bullets = remainder.split(/[•\-]/).map(l => l.trim()).filter(Boolean);
            } else {
                bullets = remainder.split(/(?<=[.!?])\s+(?=[A-Z])/).map(l => l.trim()).filter(Boolean);
            }

            return { heading, bullets };
        }

        const lines = cleanText.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length > 1) {
            return {
                heading: lines[0].replace(/\s+:\s*$/, ':'),
                bullets: lines.slice(1).map(l => l.replace(/^[•\-\*\d\.\)]+\s*/, '').trim()).filter(Boolean)
            };
        }

        return {
            heading: cleanText.replace(/\s+:\s*$/, ':'),
            bullets: detail ? detail.split('\n').map(d => d.replace(/^[•\-\*\d\.\)]+\s*/, '').trim()).filter(Boolean) : []
        };
    };

    // Calculate sample response word count
    const getSampleWordCount = (text) => {
        if (!text) return 0;
        return text.trim().split(/\s+/).filter(Boolean).length;
    };

    // Fallback high-scoring sample responses in case network/AI is temporarily unavailable
    const getFallbackSample = (taskNum, question) => {
        if (taskNum === '2') {
            const optA = question?.radioSelectOne || 'Option A';
            const optB = question?.radioSelectTwo || 'Option B';
            return {
                sampleResponse: `I would like to express my preference regarding the proposed initiative. In my opinion, choosing ${optA} would be considerably more advantageous for our entire community in the long term.\n\nFirst and foremost, ${optA} directly addresses the pressing needs of the majority of residents by offering accessible, high-quality amenities that foster social cohesion and economic vitality. Similar projects implemented in neighboring municipalities have consistently demonstrated remarkable returns on investment and high citizen satisfaction rates. Furthermore, it creates an inclusive environment that supports residents across diverse demographic groups.\n\nWhile I acknowledge the potential merits of ${optB}, particularly regarding short-term implementation convenience, its long-term benefits appear comparatively constrained. Considering our shared budgetary allocations and community growth goals, dedicating resources to the former proposal is by far the more prudent and sustainable course of action.\n\nIn conclusion, I wholeheartedly endorse ${optA} and trust that the decision-makers will give due consideration to these perspectives. Thank you for inviting our input on this vital matter.`,
                commentary: 'This response satisfies all CLB 10–12 criteria: it opens with an unambiguous statement of preference, develops two compelling arguments supported by logical reasoning, tactfully addresses the counter-option, and ends with a polite, professional conclusion. It features sophisticated lexical resources (e.g., "social cohesion", "prudent and sustainable") and flawless transitional cohesion.'
            };
        } else {
            return {
                sampleResponse: `Dear Sir or Madam,\n\nI am writing to formally address a situation regarding recent changes that have significantly affected our daily routines. I would like to explain the circumstances and suggest practical steps to resolve the matter in a mutually beneficial manner.\n\nOver the past two weeks, the unexpected schedule adjustments have caused considerable inconvenience for many individuals, as no prior notification was communicated. This lack of clear notice resulted in missed commitments and unnecessary administrative confusion. While I understand that unforeseen operational factors may arise, maintaining consistent transparency is essential for smooth cooperation.\n\nTo prevent further disruption, I propose two constructive measures. First, an automated email reminder should be dispatched at least 48 hours before any planned modifications occur. Second, establishing a direct feedback channel would enable all stakeholders to voice concerns promptly and collaborate on timely solutions.\n\nThank you for your time and understanding. I look forward to your prompt reply and hope we can implement these improvements without delay.\n\nYours sincerely,\nAlex Morgan`,
                commentary: 'This response achieves CLB 10–12 by establishing an appropriate semi-formal tone, covering all prompt requirements comprehensively, and organizing ideas with exceptional logical flow. Cohesive devices and advanced vocabulary (e.g., "operational factors", "constructive measures", "mutually beneficial") demonstrate superior communicative mastery.'
            };
        }
    };

    // Context builder for OpenAI prompt
    const buildQuestionContext = (q) => {
        if (!q) return '';
        const parts = [];
        if (q.questionLeft) parts.push(cleanQuestionLeft(q.questionLeft));
        if (q.questionRight) parts.push(q.questionRight);
        if (q.questionDetail) parts.push(q.questionDetail);
        if (q.radioSelectOne) parts.push(`Option A: ${q.radioSelectOne}`);
        if (q.radioSelectTwo) parts.push(`Option B: ${q.radioSelectTwo}`);
        return parts.filter(Boolean).join('\n\n');
    };

    // Task-specific sample fetcher
    const fetchSampleForTask = async (taskNum, forceRefresh = false) => {
        const key = `task${taskNum}`;
        const taskQuestion = questions.find(q => q.taskNumber === taskNum.toString()) ||
            (taskNum.toString() === currentQuestion?.taskNumber ? currentQuestion : null);

        if (!taskQuestion) {
            console.warn(`[Sample] No question found for Task ${taskNum}`);
            return null;
        }

        if (!forceRefresh && taskSamples[key] && taskSamples[key].questionId === taskQuestion._id) {
            return taskSamples[key];
        }

        setTaskSampleLoading(prev => ({ ...prev, [key]: true }));
        setTaskSampleErrors(prev => ({ ...prev, [key]: '' }));

        try {
            const questionContext = buildQuestionContext(taskQuestion);
            const resp = await fetch(`${API_URL}/analyzeWriting/generateSample`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question: questionContext,
                    taskType: taskNum.toString()
                })
            });

            if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
            const data = await resp.json();

            const result = {
                sampleResponse: data.sampleResponse || '',
                commentary: data.commentary || '',
                questionId: taskQuestion._id,
                taskType: taskNum.toString(),
                promptSummary: taskQuestion.questionLeft || taskQuestion.questionRight || '',
                options: taskQuestion.radioSelectOne && taskQuestion.radioSelectTwo ? {
                    optionA: taskQuestion.radioSelectOne,
                    optionB: taskQuestion.radioSelectTwo
                } : null
            };

            setTaskSamples(prev => ({ ...prev, [key]: result }));
            if (currentQuestion?.taskNumber === taskNum.toString()) {
                setSampleData(result);
            }
            return result;
        } catch (err) {
            console.error(`Error generating sample for Task ${taskNum}:`, err);
            const fallback = getFallbackSample(taskNum.toString(), taskQuestion);
            const fallbackResult = {
                ...fallback,
                questionId: taskQuestion._id,
                taskType: taskNum.toString(),
                promptSummary: taskQuestion.questionLeft || taskQuestion.questionRight || '',
                options: taskQuestion.radioSelectOne && taskQuestion.radioSelectTwo ? {
                    optionA: taskQuestion.radioSelectOne,
                    optionB: taskQuestion.radioSelectTwo
                } : null,
                isFallback: true
            };
            setTaskSamples(prev => ({ ...prev, [key]: fallbackResult }));
            if (currentQuestion?.taskNumber === taskNum.toString()) {
                setSampleData(fallbackResult);
            }
            return fallbackResult;
        } finally {
            setTaskSampleLoading(prev => ({ ...prev, [key]: false }));
        }
    };

    // Copy sample text to clipboard
    const handleCopySample = (taskNum, text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopiedSample(prev => ({ ...prev, [`task${taskNum}`]: true }));
            setTimeout(() => {
                setCopiedSample(prev => ({ ...prev, [`task${taskNum}`]: false }));
            }, 2500);
        }).catch(err => {
            console.error('Failed to copy text:', err);
        });
    };

    // Auto-fetch sample responses when test completes if not already loaded
    useEffect(() => {
        if (isTestCompleted && questions.length > 0) {
            const hasTask1 = questions.some(q => q.taskNumber === '1');
            const hasTask2 = questions.some(q => q.taskNumber === '2');

            if (hasTask1 && !taskSamples.task1 && !taskSampleLoading.task1) {
                fetchSampleForTask('1');
            }
            if (hasTask2 && !taskSamples.task2 && !taskSampleLoading.task2) {
                fetchSampleForTask('2');
            }
        }
    }, [isTestCompleted, questions]);

    // Render individual task sample card
    const renderTaskSampleCard = (taskNum, taskTitle) => {
        const key = `task${taskNum}`;
        const sample = taskSamples[key];
        const isLoading = taskSampleLoading[key];
        const error = taskSampleErrors[key];
        const isOpen = sampleOpen[key];
        const isCopied = copiedSample[key];

        const taskQuestion = questions.find(q => q.taskNumber === taskNum.toString()) ||
            (taskNum.toString() === currentQuestion?.taskNumber ? currentQuestion : null);
        const wordCount = sample?.sampleResponse ? getSampleWordCount(sample.sampleResponse) : 0;

        return (
            <div className="writing-sample-card-container">
                {/* Header / Toggle Bar */}
                <div
                    className="writing-sample-header-bar"
                    onClick={() => setSampleOpen(prev => ({ ...prev, [key]: !prev[key] }))}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span className="writing-sample-badge-clb">
                            ⭐ CLB 10–12 Model Answer
                        </span>
                        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: -0.2 }}>
                            Task {taskNum}: {taskTitle} — Sample Response & Analysis
                        </h4>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="writing-sample-toggle-pill">
                            {isOpen ? '▲ Hide Sample' : '▼ View Sample'}
                        </span>
                    </div>
                </div>

                {/* Body Content */}
                {isOpen && (
                    <div className="writing-sample-body-content">
                        {/* Loading State */}
                        {isLoading && (
                            <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                                <div style={{
                                    width: 44, height: 44, borderRadius: '50%',
                                    border: '4px solid #e2e8f0',
                                    borderTopColor: '#005a9c',
                                    animation: 'spin 0.8s linear infinite',
                                    margin: '0 auto 16px'
                                }} />
                                <h5 style={{ margin: '0 0 6px', color: '#1a4971', fontSize: 16, fontWeight: 700 }}>
                                    ✨ Generating CLB 10–12 Sample for Task {taskNum}...
                                </h5>
                                <p style={{ margin: 0, color: '#64748b', fontSize: 13.5 }}>
                                    Crafting an authentic, high-scoring model response tailored specifically to this prompt.
                                </p>
                            </div>
                        )}

                        {/* Error with Retry */}
                        {!isLoading && error && !sample && (
                            <div style={{
                                padding: '16px 20px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12
                            }}>
                                <span style={{ color: '#dc2626', fontSize: 14 }}>⚠️ {error}</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); fetchSampleForTask(taskNum, true); }}
                                    style={{
                                        padding: '6px 14px',
                                        backgroundColor: '#005a9c',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Sample Loaded */}
                        {!isLoading && sample && (
                            <div>
                                {/* Question Context Box */}
                                {taskQuestion && (
                                    <div className="writing-sample-prompt-context">
                                        <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                            <span>📋</span> Prompt Focus:
                                        </div>
                                        <div style={{ color: '#1e293b', fontSize: 13, whiteSpace: 'pre-line' }}>
                                            {cleanQuestionLeft(taskQuestion.questionLeft)?.substring(0, 240)}
                                            {(cleanQuestionLeft(taskQuestion.questionLeft)?.length || 0) > 240 ? '...' : ''}
                                        </div>
                                        {taskQuestion.radioSelectOne && taskQuestion.radioSelectTwo && (
                                            <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed #7dd3fc', fontSize: 12.5, color: '#0284c7' }}>
                                                <strong>Options:</strong> Option A: {taskQuestion.radioSelectOne} | Option B: {taskQuestion.radioSelectTwo}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Top toolbar of sample response */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 10,
                                    flexWrap: 'wrap',
                                    gap: 8
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontWeight: 800, color: '#1e293b', fontSize: 14.5 }}>
                                            Official-Standard Response:
                                        </span>
                                        <span style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: '#005a9c',
                                            background: '#e0f2fe',
                                            padding: '2px 8px',
                                            borderRadius: 12
                                        }}>
                                            {wordCount} words (Target: 150–200)
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            onClick={() => handleCopySample(taskNum, sample.sampleResponse)}
                                            style={{
                                                background: isCopied ? '#dcfce7' : '#f1f5f9',
                                                border: `1px solid ${isCopied ? '#86efac' : '#cbd5e1'}`,
                                                color: isCopied ? '#15803d' : '#334155',
                                                padding: '5px 12px',
                                                borderRadius: 6,
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 5,
                                                transition: 'all 0.15s ease'
                                            }}
                                            title="Copy sample response to clipboard"
                                        >
                                            {isCopied ? '✅ Copied!' : '📋 Copy Text'}
                                        </button>
                                        <button
                                            onClick={() => fetchSampleForTask(taskNum, true)}
                                            style={{
                                                background: '#f8fafc',
                                                border: '1px solid #cbd5e1',
                                                color: '#475569',
                                                padding: '5px 10px',
                                                borderRadius: 6,
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4
                                            }}
                                            title="Generate a new AI sample response"
                                        >
                                            🔄 Regenerate
                                        </button>
                                    </div>
                                </div>

                                {/* Sample Response Content */}
                                <div className="writing-sample-response-box">
                                    {sample.sampleResponse}
                                </div>

                                {/* Examiner Commentary */}
                                {sample.commentary && (
                                    <div className="writing-sample-commentary-box">
                                        <div style={{
                                            fontWeight: 800,
                                            color: '#15803d',
                                            fontSize: 14,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginBottom: 6
                                        }}>
                                            <span style={{ fontSize: 18 }}>💡</span>
                                            <span>Examiner Analysis — Why This Achieves CLB 10–12</span>
                                        </div>
                                        <p style={{
                                            margin: 0,
                                            fontSize: 13.5,
                                            color: '#1e3a24',
                                            lineHeight: 1.7
                                        }}>
                                            {sample.commentary}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Initial CTA if not loaded and not loading */}
                        {!isLoading && !sample && !error && (
                            <div style={{ textAlign: 'center', padding: '24px 20px' }}>
                                <p style={{ color: '#475569', fontSize: 14.5, marginBottom: 14 }}>
                                    Compare your writing with a high-scoring CLB 10–12 model response generated specifically for this task prompt.
                                </p>
                                <button
                                    onClick={() => fetchSampleForTask(taskNum)}
                                    style={{
                                        padding: '10px 24px',
                                        backgroundColor: '#005a9c',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: 6,
                                        fontWeight: 700,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 6px rgba(0,90,156,0.25)'
                                    }}
                                >
                                    ✨ Generate CLB 10–12 Model Answer
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render Completion Page
    const renderCompletion = () => {

        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                <div className="paragon-exam-subbar">
                    <div className="paragon-exam-subbar-title">
                        Practice Test {selectedTestNumber} - Writing Results
                    </div>
                </div>

                <div className="paragon-exam-body-content" style={{ padding: '32px 24px 40px' }}>
                    <div className="writing-results-page" style={{ maxWidth: 1000, margin: '0 auto', width: '100%' }}>

                        {/* Task Results with Full Analysis */}
                        <div className="writing-task-results-full">
                            {/* Task 1 Section */}
                            {taskScores.task1 && (
                                <div className="writing-task-result-section" style={{ animationDelay: '0.1s' }}>
                                    <div className="writing-task-section-header">
                                        <span className="writing-task-number-badge">Task 1</span>
                                        <span className="writing-task-title">Writing an Email</span>
                                        <span className="writing-task-score-badge">
                                            Level {Math.round(taskScores.task1?.total || taskScores.task1?.overall || taskScores.task1?.overallScore || 0)}
                                        </span>
                                    </div>
                                    <div className="writing-task-section-body">
                                        <WriscoreGraph
                                            scores={taskScores.task1}
                                            originalText={taskScores.task1?.originalText}
                                        />
                                        {renderTaskSampleCard('1', 'Writing an Email')}
                                    </div>
                                </div>
                            )}

                            {/* Task 2 Section */}
                            {taskScores.task2 && (
                                <div className="writing-task-result-section" style={{ animationDelay: '0.2s' }}>
                                    <div className="writing-task-section-header">
                                        <span className="writing-task-number-badge">Task 2</span>
                                        <span className="writing-task-title">Responding to Survey</span>
                                        <span className="writing-task-score-badge">
                                            Level {Math.round(taskScores.task2?.total || taskScores.task2?.overall || taskScores.task2?.overallScore || 0)}
                                        </span>
                                    </div>
                                    <div className="writing-task-section-body">
                                        <WriscoreGraph
                                            scores={taskScores.task2}
                                            originalText={taskScores.task2?.originalText}
                                        />
                                        {renderTaskSampleCard('2', 'Responding to Survey')}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="writing-feedback-actions">
                            <button
                                className="writing-action-btn writing-retry-btn"
                                onClick={() => window.location.reload()}
                            >
                                🔄 Retake This Test
                            </button>
                            <button
                                className="writing-action-btn writing-home-btn"
                                onClick={() => navigate('/dashboard?section=writing')}
                            >
                                ✏️ Go to Writing Tests
                            </button>
                            <button
                                className="writing-action-btn writing-dashboard-btn"
                                onClick={() => navigate('/dashboard')}
                            >
                                📊 Go to Dashboard
                            </button>
                        </div>
                    </div>
                </div>

                {renderExamFooter()}
            </div>
        );
    };

    // Render Official Paragon Top Header
    const renderExamHeader = () => (
        <div className="paragon-exam-header">
            <div className="paragon-exam-header-left">
                <img src={EngdemyLogo} alt="CELPIP" className="paragon-exam-logo" />
                <span className="paragon-exam-brand">CELPIP</span>
                <span className="paragon-exam-sep">|</span>
                <span
                    className="paragon-exam-study-link"
                    onClick={() => { stopSpeech(); navigate('/dashboard?section=writing'); }}
                >
                    Study Materials
                </span>
            </div>
            <div className="paragon-exam-header-center" style={{ fontSize: 13.5, color: '#333333', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 6 }}>Selected Product:</span>
                <select
                    value={selectedTestNumber}
                    onChange={(e) => {
                        stopSpeech();
                        const newTestNum = Number(e.target.value);
                        setSelectedTestNumber(newTestNum);
                        setShowInstructions(true);
                        setIsTestStarted(false);
                        navigate(`/writing/${newTestNum}`);
                    }}
                    className="paragon-header-select"
                >
                    {[...Array(30)].map((_, idx) => {
                        const num = idx + 1;
                        return (
                            <option key={num} value={num}>
                                CELPIP-General Practice Tests, Set {num}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="paragon-exam-header-right" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#333333' }}>
                <span style={{ fontSize: 16, color: '#4a5568' }}>⚙</span>
                <span className="paragon-exam-user-email">
                    {(currentUser?.email || currentUser?.name || "MY ACCOUNT").toUpperCase()} ▾
                </span>
            </div>
        </div>
    );

    // Render Official Paragon Subbar with NEXT and Timer
    const renderExamSubBar = () => {
        let subbarTitle = `Practice Test ${selectedTestNumber} - Writing`;
        if (!showInstructions) {
            const taskLabel = currentQuestion?.taskNumber === '2'
                ? 'Writing Task 2: Responding to Survey Questions'
                : 'Writing Task 1: Writing an Email';
            subbarTitle = `Practice Test ${selectedTestNumber} - ${taskLabel}`;
        }

        return (
            <div className="paragon-exam-subbar">
                <div className="paragon-exam-subbar-title">
                    {subbarTitle}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {!showInstructions && !isTestCompleted && (
                        <span style={{ fontSize: 13.5, color: '#333333', fontWeight: 500 }}>
                            Time remaining: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                {Math.ceil(timeRemaining / 60)} minutes
                            </strong>
                        </span>
                    )}
                    <button className="paragon-exam-next-btn" onClick={handleGlobalNext}>
                        NEXT
                    </button>
                </div>
            </div>
        );
    };

    // Render Official Writing Instructions Screen
    const renderInstructionsPage = () => (
        <div className="paragon-instructions-wrapper" style={{ width: '100%', maxWidth: 980 }}>
            <div className="paragon-instructions-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
                    <div className="paragon-instructions-header" style={{ marginBottom: 0 }}>
                        <span className="paragon-info-badge">i</span>
                        <h2 className="paragon-instructions-title" style={{ fontSize: 21, fontWeight: 800, color: '#1a4971' }}>
                            Writing Test Instructions
                        </h2>
                    </div>
                    <button
                        onClick={toggleSpeech}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            backgroundColor: isSpeaking ? '#005a9c' : '#ffffff',
                            color: isSpeaking ? '#ffffff' : '#000000',
                            border: '1.5px solid #000000',
                            borderRadius: 4,
                            padding: '6px 14px',
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                        }}
                        title="Read instructions aloud"
                    >
                        <span>{isSpeaking ? '🔊 Reading Aloud...' : '▶ Listen Instructions'}</span>
                    </button>
                </div>

                <ul className="paragon-instructions-list">
                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '12px 0 20px' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            On the official test, if you do not finish Task 1 in 27 minutes, the screen will move to Task 2. You cannot go back to Task 1. However, in this practice test, in order to move forward in the test you must click on "NEXT."
                        </span>
                    </li>

                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '12px 0' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            You have 53 minutes to complete this practice Writing Test.
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    );

    // Render Dual-Pane Active Writing Test Layout
    const renderActiveWritingTest = () => {
        if (!currentQuestion) {
            return (
                <div style={{ padding: 60, textAlign: 'center', color: '#666', width: '100%' }}>
                    <p style={{ fontSize: 18, fontWeight: 600 }}>Loading Writing Tasks...</p>
                </div>
            );
        }

        const leftText = cleanQuestionLeft(currentQuestion.questionLeft);
        const rightContent = parseRightPaneContent(currentQuestion.questionRight, currentQuestion.questionDetail);

        return (
            <div style={{ width: '100%', maxWidth: 1120 }}>
                {showFatalError && fatalErrorMsg && (
                    <div className="writing-fatal-error-banner">
                        <div className="writing-fatal-error-content">
                            <span className="writing-fatal-error-icon">💡</span>
                            <p className="writing-fatal-error-text">{fatalErrorMsg}</p>
                            <button className="writing-fatal-error-close" onClick={() => setShowFatalError(false)}>&times;</button>
                        </div>
                        <div className="writing-fatal-error-progress"></div>
                    </div>
                )}

                <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d3dae0',
                    borderRadius: 4,
                    display: 'flex',
                    minHeight: 520,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}>
                    {/* Left Pane */}
                    <div style={{
                        width: '36%',
                        padding: '24px 28px',
                        borderRight: '1px solid #dde2e6',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                                <span className="paragon-info-badge">i</span>
                                <h3 style={{ fontSize: 16.5, fontWeight: 800, color: '#1a4971', margin: 0 }}>
                                    Read the following information.
                                </h3>
                            </div>
                            <p style={{
                                fontSize: 14.5,
                                lineHeight: 1.65,
                                color: '#2d3748',
                                whiteSpace: 'pre-line',
                                margin: '0 0 20px'
                            }}>
                                {leftText}
                            </p>
                        </div>

                        {/* Sample responses box */}
                        <div
                            onClick={async () => {
                                setShowSampleModal(true);
                                const tNum = currentQuestion?.taskNumber || '1';
                                const key = `task${tNum}`;
                                if (!taskSamples[key] || taskSamples[key]?.questionId !== currentQuestion?._id) {
                                    setIsSampleLoading(true);
                                    setSampleError('');
                                    try {
                                        await fetchSampleForTask(tNum);
                                    } catch (e) {
                                        setSampleError('Could not generate a sample at this time. Please try again.');
                                    } finally {
                                        setIsSampleLoading(false);
                                    }
                                }
                            }}
                            style={{
                                marginTop: 32,
                                padding: '24px 20px',
                                border: '1px solid #c4cbd2',
                                borderRadius: 2,
                                backgroundColor: '#ffffff',
                                textAlign: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#005a9c'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#c4cbd2'; e.currentTarget.style.backgroundColor = '#ffffff'; }}
                        >
                            <span style={{ fontSize: 15, color: '#1a365d', fontWeight: 500 }}>
                                Click here to view sample responses.
                            </span>
                        </div>
                    </div>

                    {/* Right Pane */}
                    <div style={{
                        width: '64%',
                        padding: '24px 28px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                                <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                                <h3 style={{ fontSize: 16.5, fontWeight: 800, color: '#1a4971', margin: 0, lineHeight: 1.4 }}>
                                    {rightContent.heading}
                                </h3>
                            </div>

                            {rightContent.bullets.length > 0 && (
                                <ul style={{ listStyle: 'disc', paddingLeft: 34, margin: '8px 0 16px', color: '#2d3748' }}>
                                    {rightContent.bullets.map((bullet, idx) => (
                                        <li key={idx} style={{ fontSize: 14.5, lineHeight: 1.6, marginBottom: 4 }}>
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {currentQuestion.radioSelectOne && currentQuestion.radioSelectTwo && (
                                <div style={{ margin: '14px 0 16px', padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                                    <label style={{ display: 'block', marginBottom: 8, cursor: 'pointer', fontSize: 14.5, color: '#2d3748' }}>
                                        <input
                                            type="radio"
                                            name="selectOption"
                                            value="Option A"
                                            onChange={handleOptionChange}
                                            checked={selectedOption === 'Option A'}
                                            style={{ marginRight: 8, accentColor: '#005a9c' }}
                                        />
                                        <strong>Option A:</strong> {currentQuestion.radioSelectOne}
                                    </label>
                                    <label style={{ display: 'block', cursor: 'pointer', fontSize: 14.5, color: '#2d3748' }}>
                                        <input
                                            type="radio"
                                            name="selectOption"
                                            value="Option B"
                                            onChange={handleOptionChange}
                                            checked={selectedOption === 'Option B'}
                                            style={{ marginRight: 8, accentColor: '#005a9c' }}
                                        />
                                        <strong>Option B:</strong> {currentQuestion.radioSelectTwo}
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Textarea */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <textarea
                                value={answer}
                                onChange={handleTextChange}
                                placeholder="Type your response here..."
                                style={{
                                    width: '100%',
                                    minHeight: 260,
                                    padding: '14px',
                                    fontSize: 15,
                                    lineHeight: 1.6,
                                    color: '#2d3748',
                                    border: '1px solid #a0aec0',
                                    borderRadius: 3,
                                    outline: 'none',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: 14
                            }}>
                                <div style={{ fontSize: 14, color: '#4a5568', fontWeight: 600 }}>
                                    Words: <strong style={{ color: '#000000', fontSize: 15 }}>{wordCount}</strong>
                                </div>
                                <button
                                    className="submit-button"
                                    onClick={handleSubmit}
                                    style={{
                                        backgroundColor: '#005a9c',
                                        color: '#ffffff',
                                        border: '1px solid #00467a',
                                        borderRadius: 3,
                                        padding: '8px 20px',
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    Submit for Scoring
                                </button>
                            </div>

                            {isLoading ? (
                                <div style={{ textAlign: 'center', marginTop: 20 }}>
                                    <LoadingAnimation />
                                    <div style={{ marginTop: 12, fontWeight: 700, color: '#1a4971' }}>Analyzing your submission</div>
                                </div>
                            ) : (
                                showGraph && scores && (
                                    <div className="popup-overlay">
                                        <div className="popup-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                                            <button className="close-button" onClick={() => setShowGraph(false)}>
                                                &times;
                                            </button>
                                            <WriscoreGraph
                                                scores={scores}
                                                originalText={scores.originalText}
                                            />
                                            {renderTaskSampleCard(currentQuestion?.taskNumber || '1', currentQuestion?.taskNumber === '2' ? 'Responding to Survey' : 'Writing an Email')}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Sample Response Modal
    const renderSampleModal = () => {
        if (!showSampleModal) return null;
        const tNum = currentQuestion?.taskNumber || '1';
        const key = `task${tNum}`;
        const activeSample = taskSamples[key] || sampleData;
        const loading = taskSampleLoading[key] || isSampleLoading;
        const err = taskSampleErrors[key] || sampleError;
        const wordCount = activeSample?.sampleResponse ? getSampleWordCount(activeSample.sampleResponse) : 0;
        const isCopied = copiedSample[key];

        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, padding: 20
            }}>
                <div style={{
                    backgroundColor: '#ffffff', borderRadius: 8,
                    padding: '28px 32px', maxWidth: 700, width: '100%',
                    maxHeight: '88vh', overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
                }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div>
                            <h3 style={{ margin: 0, color: '#1a4971', fontSize: 18, fontWeight: 800 }}>
                                Task {tNum}: Sample Response — CLB Level 10–12
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                                Generated specifically for this question
                            </p>
                        </div>
                        <button
                            onClick={() => setShowSampleModal(false)}
                            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#666', lineHeight: 1 }}
                        >
                            &times;
                        </button>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: '50%',
                                border: '4px solid #e2e8f0',
                                borderTopColor: '#005a9c',
                                animation: 'spin 0.8s linear infinite',
                                margin: '0 auto 16px'
                            }} />
                            <p style={{ color: '#1a4971', fontWeight: 600, fontSize: 15, margin: 0 }}>
                                ✨ Generating a sample for this question...
                            </p>
                            <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>This may take a few seconds</p>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && err && !activeSample && (
                        <div style={{
                            padding: '16px', background: '#fff5f5', border: '1px solid #fca5a5',
                            borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16
                        }}>
                            ⚠️ {err}
                        </div>
                    )}

                    {/* Sample Content */}
                    {!loading && activeSample && (
                        <div style={{ fontSize: 14.5, lineHeight: 1.75, color: '#2d3748' }}>
                            {/* Question context reminder */}
                            <div style={{
                                backgroundColor: '#f0f9ff', borderLeft: '3px solid #0ea5e9',
                                padding: '10px 14px', borderRadius: 4, marginBottom: 18, fontSize: 13, color: '#0369a1'
                            }}>
                                <strong>📋 Question context:</strong>{' '}
                                {cleanQuestionLeft(currentQuestion?.questionLeft)?.substring(0, 180)}{(cleanQuestionLeft(currentQuestion?.questionLeft)?.length || 0) > 180 ? '...' : ''}
                                {currentQuestion?.radioSelectOne && currentQuestion?.radioSelectTwo && (
                                    <div style={{ marginTop: 6, fontSize: 12.5, color: '#0284c7' }}>
                                        <strong>Options:</strong> Option A: {currentQuestion.radioSelectOne} | Option B: {currentQuestion.radioSelectTwo}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <p style={{ fontWeight: 700, color: '#1a365d', margin: 0, fontSize: 15 }}>
                                    ⭐ High-Scoring Sample Response (CLB 10–12):
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 12, color: '#005a9c', background: '#e0f2fe', padding: '2px 8px', borderRadius: 12, fontWeight: 700 }}>
                                        {wordCount} words
                                    </span>
                                    <button
                                        onClick={() => handleCopySample(tNum, activeSample.sampleResponse)}
                                        style={{
                                            background: isCopied ? '#dcfce7' : '#f1f5f9',
                                            border: `1px solid ${isCopied ? '#86efac' : '#cbd5e1'}`,
                                            color: isCopied ? '#15803d' : '#334155',
                                            padding: '4px 10px',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {isCopied ? '✅ Copied' : '📋 Copy'}
                                    </button>
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#f8fafc',
                                borderLeft: '4px solid #005a9c',
                                padding: '16px 18px', borderRadius: 6,
                                marginBottom: 18,
                                whiteSpace: 'pre-line', lineHeight: 1.8,
                                color: '#1e293b', fontSize: 14.5
                            }}>
                                {activeSample.sampleResponse}
                            </div>

                            {activeSample.commentary && (
                                <div style={{ backgroundColor: '#f0fdf4', padding: '14px 16px', borderRadius: 6, border: '1px solid #86efac' }}>
                                    <strong style={{ color: '#15803d', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        <span>💬</span> Examiner Commentary:
                                    </strong>
                                    <p style={{ margin: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.65 }}>
                                        {activeSample.commentary}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={() => setShowSampleModal(false)}
                        style={{
                            marginTop: 22, width: '100%', padding: '10px',
                            backgroundColor: '#005a9c', color: '#fff',
                            border: 'none', borderRadius: 4, fontWeight: 700, cursor: 'pointer'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    // Render Format Modal when clicking "here"
    const renderFormatModal = () => {
        if (!showFormatModal) return null;
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: 20
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    padding: '28px 32px',
                    maxWidth: 550,
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}>
                    <h3 style={{ margin: '0 0 16px', color: '#1a4971', fontSize: 20, fontWeight: 700 }}>
                        CELPIP Writing Test Format
                    </h3>
                    <div style={{ fontSize: 15, lineHeight: 1.7, color: '#333333' }}>
                        <p style={{ margin: '0 0 12px' }}>
                            <strong>Task 1: Writing an Email</strong><br />
                            Time: 27 minutes<br />
                            Length: 150–200 words
                        </p>
                        <p style={{ margin: '0 0 16px' }}>
                            <strong>Task 2: Responding to Survey Questions</strong><br />
                            Time: 26 minutes<br />
                            Length: 150–200 words
                        </p>
                        <p style={{ margin: 0, color: '#555' }}>
                            Total Test Duration: <strong>53 minutes</strong>
                        </p>
                    </div>
                    <button
                        onClick={() => setShowFormatModal(false)}
                        style={{
                            marginTop: 24,
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#005a9c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    // Render Official Paragon Bottom Bar with BACK
    const renderExamBottomBar = () => (
        <div className="paragon-exam-bottombar">
            <button className="paragon-exam-back-btn" onClick={handleGlobalBack}>
                BACK
            </button>
        </div>
    );

    // Render Official Footer
    const renderExamFooter = () => (
        <div className="paragon-exam-footer" style={{
            textAlign: 'center',
            padding: '16px 20px 24px',
            fontSize: 12.5,
            color: '#6c757d',
            backgroundColor: '#eaedf1'
        }}>
            Copyright 2025 - Engdemy | www.celpip.ca | Contact Us
        </div>
    );

    if (isTestCompleted) {
        return renderCompletion();
    }

    return (
        <div className="paragon-exam-page-wrapper">
            {renderExamHeader()}
            {renderExamSubBar()}

            <div className="paragon-exam-body-content">
                {showInstructions ? (
                    renderInstructionsPage()
                ) : (
                    renderActiveWritingTest()
                )}
            </div>

            {renderExamBottomBar()}
            {renderExamFooter()}
            {renderFormatModal()}
            {renderSampleModal()}
        </div>
    );
};

export default WritingTest;


